<template>
  <div>
    
    <section class="section is-medium">
      <div class="container">
        <h1 class="title">Account Settings</h1>
        <div class="tabs is-boxed mb-0">
          <ul>
            <li v-bind:class="{ 'is-active': activeTab == 0 }">
              <a @click.prevent="activeTab = 0">General</a>
            </li>
            <li v-bind:class="{ 'is-active': activeTab == 1 }">
              <a @click.prevent="activeTab = 1">Newsletter Survey Defaults</a>
            </li>
            <li v-bind:class="{ 'is-active': activeTab == 2 }">
              <a @click.prevent="activeTab = 2">Team Members</a>
            </li>
            <li v-bind:class="{ 'is-active': activeTab == 3 }">
              <a @click.prevent="activeTab = 3">Billing</a>
            </li>
          </ul>
        </div>

<div class="tab-panel">
        <div v-if="activeTab == 0">
          <div class="columns">
              <div class="column is-half">
                <p class="mb-4">General settings can only be changed by the owner of the account.</p>
                <div  class="field">
                  <label class="label" for="name">Name</label>
                  <div class="control">
                    <input
                      v-model="accountName"
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Give your account a name that you remember"
                      class="input"
                      required
                      :disabled="!account.is_owner"
                    />
                  </div>
                  <p class="help">
                    Give your account a name you and your team members recognize.
                  </p>
                </div>
              
                <div class="field is-grouped mt-4 mb-6">
                    <div class="control">
                      <button
                        :disabled="!saveGeneralBtnEnabled || !account.is_owner"
                        v-bind:class="{ 'is-loading': isSaving }"
                        class="button is-primary is-outlined"
                        @click.prevent="saveGeneralSettings"
                      >
                        Save Account Settings
                      </button>
                    </div>
                  </div>
                  </div>
          </div>
        </div>
        <div v-if="activeTab == 1">
          <p class="mb-5">Set default values for new Newsletter Surveys.</p>
          <p class="mb-5 notification is-info is-light">
              Changes are only valid for new Newsletter Survey. Existing surveys will not be updated automatically.
              You can update those under <router-link
                  :to="{ name: 'FeedbackList' }"
                  >All Surveys</router-link
                > (see Sidebar on the left).
          </p>
          <div class="field">
            <label class="label" for="name">Your Name</label>
            <div class="control">
              <input
                v-model="feedbackForm.name"
                id="name"
                name="name"
                type="text"
                placeholder="Name shown on the extended feedback page"
                class="input"
                required
              />
            </div>
          </div>
          <FormCustomSection
            :feedbackForm="feedbackForm"
          />
          <div class="field is-grouped">
            <div class="control">
              <button
                v-bind:class="{ 'is-loading': isSaving }"
                class="button is-primary is-outlined is-medium"
                @click.prevent="saveDefaults"
              >
                Save Defaults
              </button>
            </div>
          </div>

        </div>
        <div v-if="activeTab == 2">
          <div class="columns">
              <div class="column is-half">
                <p class="mb-4">Team members can only be changed by the owner of the account.</p>
                <div v-if="account.is_owner">
                  <p class="mb-4">Current team members:</p>
                  <div class="table-container">
                    <table class="table is-fullwidth">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Role</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(member, index) in members" v-bind:key="index">
                          <td>{{member.email}}</td>
                          <td>{{member.role}}</td>
                          <td>
                            <button
                              v-if="account.is_owner && member.role !== 'owner'"
                              @click.prevent="removeMember(member.user_id)"
                              class="button is-danger is-small"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p class="my-3">Add a new team member:</p>
                  <div v-if="limitReached" class="box m-1 has-text-centered content">
                    <p>
                      The maximum number of team members on your plan is reached. Upgrade to Professional or Business to add more.
                    </p>
                    <p>
                      <router-link
                        :to="{ name: 'Upgrade' }"
                        class="button is-small is-info is-inline"
                        >Unlock Now</router-link
                      >
                    </p>
                  </div>
                  <div v-else>
                  <div class="field has-addons">

                    <div class="control is-expanded">
                      <input
                        v-model="newMemberEmail"
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Email of the new team member"
                        class="input"
                        :class="{ 'is-danger': registerErrorMsg }"
                        required
                      />
                    </div>
                    <div class="control">
                      <button
                        :disabled="(!newMemberEmail || !account.is_owner) || limitReached"
                        v-bind:class="{ 'is-loading': isSaving }"
                        class="button is-primary"
                        @click.prevent="addMember"
                      >
                        Add Team Member
                      </button>
                    </div>
                  </div>

                  <p v-if="registerErrorMsg" class="help is-danger">This email address is invalid</p>

                  <p class="help">
                    Team members will have a Admin access by default and can do everything except changing general account settings, billing and managing team members.
                  </p>
                  <p class="mt-4">
                    If the team member has an user account already, it will be used and the user will be added to the team. Otherwise, a new user account will be created and the user receives a activation email.
                  </p>
                  </div>
                </div>
              </div>
          </div>
        </div>
        <div v-if="activeTab == 3">
          <p class="mb-4">Billing details can only be changed by the owner of the account.</p>
          <div v-if="account.is_owner" class="content">

            <p><b>Your plan</b>: 
            <span v-if="userDetails.subscribed != true">FREE</span> <router-link v-if="!userDetails.subscribed" :to="{ name: 'Upgrade'}" class="tag is-info">Upgrade Now</router-link>
            <span v-if="userDetails.subscribed == true">{{users_plan.name }} @ {{users_plan.price }} USD per {{users_plan.period }}</span>
            <br>
            <span v-if="userDetails.subscribed && !account.is_current_plan" class="tag is-large is-warning">Grandfathered in</span>
            </p>


            
            <div v-if="userDetails.subscribed == true">
              <div v-if="userDetails.cancellation_effective_date">
                Your subscription is cancel to {{cancenllationDateForHuman}}
              </div>
              <template v-else>
                <p>Update Your Subscription</p>
                <ul>
                <li><a :href="userDetails.update_url">Update Billing Details</a></li>
                <li><a :href="userDetails.cancel_url" >Cancel subscription</a><br>
                Sad to see you go but not hard feelings. Simply cancel here.
                </li>
                </ul>
              </template>
            </div>
            
          </div>
        </div>
</div>
      </div>
    </section>
  </div>
</template>

<script>
import atomic from "atomicjs";
import {trackEvent}  from "../lib/flevents"

import {plans} from "@/plans-settings";
import FormCustomSection from "@/components/FormCustomizationSection.vue";
import { mapState } from "vuex";
export default {
  name: "account",
  components: { FormCustomSection },
  data: function () {
    return {
      activeTab: 0,
      isSaving: false,
      saveGeneralBtnEnabled: false,
      feedbackForm: {
        name: null,
        thumbsup: "👍 That helped me. Thanks",
        thumbsok: "😐 Meh - was ok.",
        thumbsdown: "👎 Not interesting to me.",
        thumbsHeadline: "Was it useful? Help us to improve!",
        thumbsParagraph:
          "With your feedback, we can improve the letter. Click on a link to vote:",

        efpThumbsupReasonHeader: "👍 you did enjoy NAME email 👍",
        efpThumbsokReasonHeader:
          "😐 you are not sure what to think about NAME email 😐",
        efpThumbsdownReasonHeader: "👎 you didn’t enjoy NAME email 👎",
        efpThankYouText: "Thank you. Your feedback was successfully shared.",
        efpWhyBoxText: "Why",
        efpWhyBoxPlaceholder: "Listen to your gut :-)",
        efpButtonText: "Send your message",
        efpReaderNamePlaceholder:
          "Enter your name or leave it blank to stay anonymous",
        efpReaderEmailPlaceholder: "Enter your email if you want a reply from me",
        efpTextMsgText: "write a text message instead",
        efpVoiceText: "or leave a voice message",
        efpAllowAudioFeedback: "1",
        efpVoiceRemainingText: "Record duration is limited",
        efpNotificationEmail: null,
        efpNotificationWebhookURL: null,
        typHeadline: "Thank you for sharing your thoughts",
        typSubHeadline: "We wish you a wonderful day and stay safe!",
        typRedirect: null,
        typShareButtonTwitter: "Share your feedback on Twitter",
        typShareTwitterHandle: null,
        thumbsupLabel: "",
        thumbsokLabel: "",
        thumbsdownLabel: "",
        thumbsoption4: null,
        efpThumbsoption4ReasonHeader: null,
        thumbsoption5: null,
        efpThumbsoption5ReasonHeader: null,
        showBranding: "1",
        efpEnabled: "1",
        dailyVotes: {},
        formType: "newsletter",
        efpAllowBlankFeedback: "0",
        efpPrefillEmail: "1",
      },
      userDetails: {},
      accountName: "",
      members: [],
      newMemberEmail: "",
      registerErrorMsg: "",
    };
  },
  mounted: function () {
    if (!this.user.isLoggedIn) {
      this.$router.push("/login");
      return
    }
    if (this.userLoaded === true) {
      this.loadUserDetails();
      this.loadMembers();
    }

    trackEvent(this.$store.state.user.id, "account-view")
  },
  computed: {
    ...mapState(["user", "account", "userLoaded"]),
    users_plan() {
      return plans[this.account.subscription_plan_id]
    },

    cancenllationDateForHuman() {
      const date = new Date(this.userDetails.cancellation_effective_date);

      // Specify options to display the full month name
      const options = { month: 'long', day: 'numeric', year: 'numeric' };

      // Use Intl.DateTimeFormat with desired locale, e.g., 'en-US' for English
      return new Intl.DateTimeFormat('en-US', options).format(date);
    },
    memberLimit() {
      if (this.account.has_biz_features) {
        return 5
      } else if(this.account.has_pro_features) {
        return 2
      }
      return 1
    },
    limitReached() {
      if (this.members.length >= this.memberLimit) {
        return true
      }
      return false
    }
  },
  watch: {
    activeTab: function (val) {
      trackEvent(this.$store.state.user.id, "account-maintab-view-" + this.activeTab)
    },
    accountName: function (val) {
      console.log("account name changed", val)
      this.saveGeneralBtnEnabled = this.account.subscribed && val !== null && val !== "";
    },
    userLoaded: function (val) {
      if (val === true) {
        console.log("user loaded")
        this.loadUserDetails();
        this.loadMembers();
      }
    }
  },
  methods: {
    saveGeneralSettings: function () {
      this.isSaving = true;
      var self = this;
      this.$store.dispatch('getAPIToken')
        .then((token) => {
          atomic( self.apiHost + `account/${this.account.id}`, {
            method: "PUT",
            headers: {
              Authorization: "Bearer " + self.user.app_metadata.api_token,
              "Content-type": "application/json",
            },
            data: {
              name: self.accountName,
            }
          }).then(async function (response) {
            console.log("yo", response.data)
            await self.$store.dispatch('updateAccountName', self.accountName)

            self.$buefy.toast.open({
              duration: 3000,
              message: `General settings successfully updated`,
              type: 'is-success'
            })
            self.isSaving = false;
          })
          .catch(function (error) {
              self.isSaving = false;
              self.$buefy.toast.open({
                duration: 3000,
                message: `Updating your General settings failed. It was us, not you and the team was already notified. Please try again later.`,
                type: 'is-danger'
              })
              console.log(error.status); // xhr.status
              console.log(error.statusText); // xhr.statusText
            });
        })
        .catch((error) => {
          this.$parent.$emit("unauthed")
        });
    },
    saveDefaults: function () {
      trackEvent(this.$store.state.user.id, "account-defaults-save")
      this.isSaving = true;
      var self = this;
      var userSettings = {
        presetName: this.feedbackForm.name,
        presetThumbsupText: this.feedbackForm.thumbsup,
        presetThumbsokText: this.feedbackForm.thumbsok,
        presetThumbsdownText: this.feedbackForm.thumbsdown,
        presetThumbsHeadline: this.feedbackForm.thumbsHeadline,
        presetThumbsParagraph: this.feedbackForm.thumbsParagraph,

        presetThumbsoption4Text: this.feedbackForm.thumbsoption4,
        presetEfpThumbsoption4ReasonHeader:
          this.feedbackForm.efpThumbsoption4ReasonHeader,
        presetThumbsoption5Text: this.feedbackForm.thumbsoption5,
        presetEfpThumbsoption5ReasonHeader:
          this.feedbackForm.efpThumbsoption5ReasonHeader,
        presetEfpThumbsupReasonHeader: this.feedbackForm.efpThumbsupReasonHeader,
        presetEfpThumbsokReasonHeader: this.feedbackForm.efpThumbsokReasonHeader,
        presetEfpThumbsdownReasonHeader:
          this.feedbackForm.efpThumbsdownReasonHeader,
        presetEfpThankYouText: this.feedbackForm.efpThankYouText,
        presetEfpWhyBoxText: this.feedbackForm.efpWhyBoxText,
        presetEfpWhyBoxPlaceholder: this.feedbackForm.efpWhyBoxPlaceholder,
        presetEfpButtonText: this.feedbackForm.efpButtonText,
        presetEfpReaderNamePlaceholder:
          this.feedbackForm.efpReaderNamePlaceholder,
        presetEfpReaderEmailPlaceholder:
          this.feedbackForm.efpReaderEmailPlaceholder,
        presetEfpTextMsgText:
          this.feedbackForm.efpTextMsgText,
        presetEfpVoiceText:
          this.feedbackForm.efpVoiceText,
        presetEfpAllowAudioFeedback: this.feedbackForm.efpAllowAudioFeedback,
        presetEfpVoiceRemainingText:
          this.feedbackForm.efpVoiceRemainingText,
        presetEfpNotificationEmail: this.feedbackForm.efpNotificationEmail,
        presetEfpNotificationWebhookURL: this.feedbackForm.efpNotificationWebhookURL,
        presetTypHeadline: this.feedbackForm.typHeadline,
        presetTypSubHeadline: this.feedbackForm.typSubHeadline,
        presetTypRedirect: this.feedbackForm.typRedirect,
        presetTypShareButtonTwitter: this.feedbackForm.typShareButtonTwitter,
        presetTypShareTwitterHandle: this.feedbackForm.typShareTwitterHandle,
        presetShowBranding: this.feedbackForm.showBranding || "1",
        presetEfpEnabled: this.feedbackForm.efpEnabled || "1",
        presetEfpAllowBlankFeedback: this.feedbackForm.efpAllowBlankFeedback || "0",
        presetEfpPrefillEmail: this.feedbackForm.efpPrefillEmail || "1"
      };
      if (!this.$parent.account.subscribed) {
        userSettings.presetEfpThankYouText = this.feedbackForm.efpThankYouText;
        userSettings.presetEfpWhyBoxText = this.feedbackForm.efpWhyBoxText;
        userSettings.presetEfpWhyBoxPlaceholder =
          this.feedbackForm.efpWhyBoxPlaceholder;
        userSettings.presetEfpButtonText = this.feedbackForm.efpButtonText;
        userSettings.presetEfpReaderNamePlaceholder =
          this.feedbackForm.efpReaderNamePlaceholder;
        userSettings.presetEfpReaderEmailPlaceholder =
          this.feedbackForm.efpReaderEmailPlaceholder
        userSettings.presetTypHeadline = this.feedbackForm.typHeadline;
        userSettings.presetTypSubHeadline = this.feedbackForm.typSubHeadline;
        userSettings.presetTypRedirect = null;
        userSettings.presetEfpNotificationEmail = null;
        userSettings.presetEfpNotificationWebhookURL = null,
        userSettings.presetThumbsoption4Text = null;
        userSettings.presetEfpThumbsoption4ReasonHeader = null;
        userSettings.presetThumbsoption5Text = null;
        userSettings.presetEfpThumbsoption5ReasonHeader = null;
      }
      let userSettingsData = {
        presets: userSettings
      }
      this.$store.dispatch('getAPIToken')
        .then((token) => {
          atomic( self.apiHost + `account/${this.account.id}`, {
            method: "PUT",
            headers: {
              Authorization: "Bearer " + self.user.app_metadata.api_token,
              "Content-type": "application/json",
            },
            data: userSettingsData,
          }).then(async function (response) {
            console.log("yo", response.data)
            await self.$store.dispatch('updateFeedformPresets', response.data)

            self.$buefy.toast.open({
              duration: 3000,
              message: `Newsletter Survey Defaults successfully updated`,
              type: 'is-success'
            })
            self.isSaving = false;
          })
          .catch(function (error) {
              self.isSaving = false;
              self.$buefy.toast.open({
                duration: 3000,
                message: `Updating your Newsletter Survey Defaults failed. It was us, not you and the team was already notified. Please try again later.`,
                type: 'is-danger'
              })
              console.log(error.status); // xhr.status
              console.log(error.statusText); // xhr.statusText
            });
        })
        .catch((error) => {
          this.$parent.$emit("unauthed")
        });
    },
    loadMembers: async function () {
      var self = this;
      this.$store.dispatch('getAPIToken')
        .then((token) => {
          atomic( self.apiHost + `account/${this.account.id}/members`, {
            method: "GET",
            headers: {
              Authorization: "Bearer " + self.user.app_metadata.api_token,
              "Content-type": "application/json",
            },
          })
            .then(function (response) {
              self.members = response.data;
            })
            .catch(function (error) {
              console.log(error.status); // xhr.status
              console.log(error.statusText); // xhr.statusText
            });
        })
        .catch((error) => {
          this.$parent.$emit("unauthed")
        });
    },
    addMember: async function () {
      var self = this;
      this.isSaving = true;
      this.$store.dispatch('getAPIToken')
        .then((token) => {
          atomic( self.apiHost + `account/${this.account.id}/members`, {
            method: "POST",
            headers: {
              Authorization: "Bearer " + self.user.app_metadata.api_token,
              "Content-type": "application/json",
            },
            data: {
              email: self.newMemberEmail
            }
          })
            .then(function (response) {
              self.members.push(response.data);
              self.newMemberEmail = null;
              self.isSaving = false;
              self.$buefy.toast.open({
                duration: 3000,
                message: `Member successfully added`,
                type: 'is-success'
              })
            })
            .catch(function (error) {
              if (error.responseText.trim() == "ERROR_INVALID_EMAIL") {
                self.registerErrorMsg = "Not a valid email address."
                self.isSaving = false;
              } else {
                self.$buefy.toast.open({
                  duration: 3000,
                  message: `Sorry but could not add the member. It was us, not you and the team was already notified. Please try again later.`,
                  type: "is-danger",
                });
              }

              console.log(error.status); // xhr.status
              console.log(error.statusText); // xhr.statusText
            });
        })
        .catch((error) => {
          this.$parent.$emit("unauthed")
        });
    },
    removeMember: async function (user_id) {
      this.isSaving = true;
      this.$buefy.dialog.confirm({
          message:
            "Are you sure you want to remove the team member?",
          type: "is-danger",
          onConfirm: async () => {
            console.log("confirmed delete dialog");
            var self = this;
            this.$store.dispatch('getAPIToken')
              .then((token) => {
                atomic( self.apiHost + `account/${this.account.id}/members/${user_id}`, {
                  method: "DELETE",
                  headers: {
                    Authorization: "Bearer " + self.user.app_metadata.api_token,
                    "Content-type": "application/json",
                  },
                })
                  .then(function (response) {
                    self.members = self.members.filter((member) => member.user_id !== user_id);
                    self.isSaving = false;
                    self.$buefy.toast.open({
                      duration: 3000,
                      message: `Member successfully removed`,
                      type: 'is-success'
                    })
                  })
                  .catch(function (error) {
                    self.$buefy.toast.open({
                      duration: 3000,
                      message: `Sorry but could not remove the member. It was us, not you and the team was already notified. Please try again later.`,
                      type: "is-danger",
                    });
                    console.log(error.status); // xhr.status
                    console.log(error.statusText); // xhr.statusText
                  });
              })
              .catch((error) => {
                this.$parent.$emit("unauthed")
              });
          }
      })
    },
    loadUserDetails: async function () {
        var self = this;
              this.$store.dispatch('getAPIToken')
        .then((token) => {
            //console.log("get user details");
            atomic(self.apiHost +  `account/${this.account.id}`, {
              method: "GET",
              headers: {
                Authorization: "Bearer " +  token,
                "Content-type": "application/json",
              },
            })
              .then(function (response) {
                // user defaulfts presets -> feedbackForm format because of widget reuse

                if (Object.keys(response.data.presets).length > 0) {
                  self.$set(self.feedbackForm, "name", response.data.presets.presetName);
                  self.$set(
                    self.feedbackForm,
                    "thumbsup",
                    response.data.presets.presetThumbsupText
                  );
                  self.$set(
                    self.feedbackForm,
                    "thumbsok",
                    response.data.presets.presetThumbsokText
                  );
                  self.$set(
                    self.feedbackForm,
                    "thumbsdown",
                    response.data.presets.presetThumbsdownText
                  );
                  self.$set(
                    self.feedbackForm,
                    "thumbsoption4",
                    response.data.presets.presetThumbsoption4Text
                  );
                  self.$set(
                    self.feedbackForm,
                    "thumbsoption5",
                    response.data.presets.presetThumbsoption5Text
                  );
                  self.$set(
                    self.feedbackForm,
                    "thumbsHeadline",
                    response.data.presets.presetThumbsHeadline
                  );
                  self.$set(
                    self.feedbackForm,
                    "thumbsParagraph",
                    response.data.presets.presetThumbsParagraph
                  );

                  self.$set(
                    self.feedbackForm,
                    "efpThumbsupReasonHeader",
                    response.data.presets.presetEfpThumbsupReasonHeader
                  );
                  self.$set(
                    self.feedbackForm,
                    "efpThumbsokReasonHeader",
                    response.data.presets.presetEfpThumbsokReasonHeader
                  );
                  self.$set(
                    self.feedbackForm,
                    "efpThumbsdownReasonHeader",
                    response.data.presets.presetEfpThumbsdownReasonHeader
                  );
                  self.$set(
                    self.feedbackForm,
                    "efpThumbsoption4ReasonHeader",
                    response.data.presets.presetEfpThumbsoption4ReasonHeader
                  );
                  self.$set(
                    self.feedbackForm,
                    "efpThumbsoption5ReasonHeader",
                    response.data.presets.presetEfpThumbsoption5ReasonHeader
                  );
                  self.$set(
                    self.feedbackForm,
                    "efpThankYouText",
                    response.data.presets.presetEfpThankYouText
                  );
                  self.$set(
                    self.feedbackForm,
                    "efpWhyBoxText",
                    response.data.presets.presetEfpWhyBoxText
                  );
                  self.$set(
                    self.feedbackForm,
                    "efpWhyBoxPlaceholder",
                    response.data.presets.presetEfpWhyBoxPlaceholder
                  );
                  self.$set(
                    self.feedbackForm,
                    "efpButtonText",
                    response.data.presets.presetEfpButtonText
                  );
                  self.$set(
                    self.feedbackForm,
                    "efpReaderNamePlaceholder",
                    response.data.presets.presetEfpReaderNamePlaceholder
                  );
                  self.$set(
                    self.feedbackForm,
                    "efpReaderEmailPlaceholder",
                    response.data.presets.presetEfpReaderEmailPlaceholder
                  )
                  if(response.data.presets.presetEfpTextMsgText) {
                    self.$set(
                      self.feedbackForm,
                      "efpTextMsgText",
                      response.data.presets.presetEfpTextMsgText
                    );
                  }
                  if (response.data.presets.presetEfpVoiceText) {
                    self.$set(
                      self.feedbackForm,
                      "efpVoiceText",
                      response.data.presets.presetEfpVoiceText
                    );
                  }
                  if (response.data.presets.presetEfpAllowAudioFeedback) {
                    self.$set(
                      self.feedbackForm,
                      "efpAllowAudioFeedback",
                      response.data.presets.presetEfpAllowAudioFeedback
                    );
                  }

                  if(response.data.presets.presetEfpVoiceRemainingText) {
                    self.$set(
                      self.feedbackForm,
                      "efpVoiceRemainingText",
                      response.data.presets.presetEfpVoiceRemainingText
                    );
                  }
                  self.$set(
                    self.feedbackForm,
                    "typHeadline",
                    response.data.presets.presetTypHeadline
                  );
                  self.$set(
                    self.feedbackForm,
                    "typSubHeadline",
                    response.data.presets.presetTypSubHeadline
                  );
                  self.$set(
                    self.feedbackForm,
                    "typRedirect",
                    response.data.presets.presetTypRedirect
                  );
                  self.$set(
                    self.feedbackForm,
                    "typShareTwitterHandle",
                    response.data.presets.presetTypShareTwitterHandle
                  )
                  self.$set(
                    self.feedbackForm,
                    "typShareButtonTwitter",
                    response.data.presets.presetTypShareButtonTwitter
                  )
                  
                  self.$set(
                    self.feedbackForm,
                    "efpNotificationEmail",
                    response.data.presets.presetEfpNotificationEmail
                  );
                  self.$set(
                    self.feedbackForm,
                    "efpNotificationWebhookURL",
                    response.data.presets.presetEfpNotificationWebhookURL
                  );
                  
                  self.$set(
                    self.feedbackForm,
                    "showBranding",
                    response.data.presets.presetShowBranding || "1"
                  );

                  self.$set(
                    self.feedbackForm,
                    "efpEnabled",
                    response.data.presets.presetEfpEnabled || "1"
                  );
                  self.$set(
                    self.feedbackForm,
                    "efpAllowBlankFeedback",
                    response.data.presets.presetEfpAllowBlankFeedback || "0"
                  );

                  self.$set(
                    self.feedbackForm,
                    "efpPrefillEmail",
                    response.data.presets.presetEfpPrefillEmail || "1"
                  );
                }
                self.userDetails = response.data
                self.accountName = response.data.name
                // xhr.responseText
                //self.$emit("setUserDetails", self.user);
                console.log(response.data); // xhr.responseText
                //console.log(response.xhr); // full response
              })
              .catch(function (error) {
                console.log(error.status); // xhr.status
                console.log(error.statusText); // xhr.statusText
              });
          })
        .catch((error) => {
          this.$parent.$emit("unauthed")
        });
      

      return;
    },
  },
};
</script>

